/**
 * @fileoverview The plants brief: everything the Plants page decides about
 * the garden's plants, worked out by the backend (services/plants/
 * plantsBrief; D40) and sent ready to show, so the page only lays it out.
 * Each live plant's planting stage, its week said (the state and its colour,
 * each mark with its colour and words: what, chip, title; the "Grows slowly"
 * item's title; how much it asks; the card's paragraphs under its grid, which
 * way each slow half leans, the range it grows best in: batch 4 of the
 * architecture audit, A26), and its planting dates said (the card's line and
 * the sheet of every window), what counts as rain on the card's rain row
 * (rainShownInches, the one rain-day Grow setting; A25); the garden's marks
 * (the cards' chips gathered by what they are about); the "Needs you
 * first" order over every plant, live and archived; and whether Grow knows
 * each plant's crop, so its card offers About this plant (`about` on each
 * live plant, `aboutArchived` for the archived ones; W24 Job A: only the
 * yes or no, the crop's timings and frost fields stay on the server). B136 added, for the plant card: each week's caption
 * (lead, closed sentence, story, "more" and "less") and the icon the server names for it, the count of the plant's to-dos due
 * today, and the Care strip's two facts.
 *
 * Where: shared/hooks/usePlantsBrief (the one read), and through it the
 * Plants page's cards, tiles, rows and grid, the card's weather caption
 * (WeekCaption), the planting dates line, the Care strip and the tiles, and the beds' plant rows
 * (LocationPlantList).
 * API: GET /plants/brief?zip=&lang=&unit= (the brief is for the garden's own day, the server's).
 *
 * @module api/plantsBrief
 */

import { apiClient } from './apiClient';
import { gardenZip } from './gardenMembers';
import type { PlantSharing } from './shares';
import type { OutlookHalf, WeekLevel } from './outlook';
import type { VerdictToken } from '@/shared/theme/verdictTokens';

/** A plant's planting stage, as recorded: still to sow or plant, started and still to plant out, nothing recorded, or past planting. */
export type PlantingStage = 'planning' | 'seeding' | 'not_sure' | null;

/** One thing a plant's week holds, said. */
export interface BriefMark {
  /** What it is about, the same across plants: "danger-PROTECT_FROST", "harm-chill", "downpour", "excess_rain", "needs_water". */
  family: string;
  /** A danger, harm, or water. */
  kind: 'danger' | 'harm' | 'water';
  /** For harm by temperature: the grading's reason. */
  basis?: 'chill' | 'snap' | 'spike';
  /** Temperature or water, with code, for its icon. */
  iconKind: 'temperature' | 'water';
  /** The verdict code its icon is drawn from. */
  code: string;
  /** Its colour; null for one the theme has no token for (wind). */
  token: VerdictToken | null;
  /** A danger: drawn solid. */
  solid: boolean;
  /** Harm: drawn outlined. */
  harm: boolean;
  /** The verdicts it stands for, to find their care in the outlook's advice. */
  refs: Array<{ code: string; timeOfDay: 'day' | 'night' | null }>;
  /** The days and nights it falls on, in order. */
  halves: OutlookHalf[];
  /** The days, for rain. */
  dates: string[];
  /** For downpours: the week's rain is too much as well. */
  tooMuchRain?: boolean;
  /** Its words alone: "frost", "chilly night", "water". */
  what: string;
  /** With its days, short, for the folded chips: "Frost Thu night". */
  chip: string;
  /** With its days in a sentence: "Frost, Mon and Tue nights". */
  title: string;
}

/** The week's state and the colour it leans, as the bar, the label and the badge draw it. */
export interface WeekStatus {
  /** Thriving, OK, Slowed, Stressed or At risk. */
  state: WeekLevel;
  /** Thriving: good. Stressed: the way it leans. At risk: its danger's colour, or null for wind. Otherwise null. */
  token: VerdictToken | null;
}

/** A half of the week that only slows the plant, and which way it leans on the grid (the server's: cool under where it grows best, warm over). */
export interface SlowHalf extends OutlookHalf {
  /** The tint's side; null for a half with no reading or a plant with no range. */
  lean: 'cool' | 'warm' | null;
}

/** One item under the card's grid: a title in its colour and one paragraph, written by the server (services/plants/plantWeekItems). */
export interface BriefWeekItem {
  /** Unique among the items: the mark's family, 'slow' or 'water'. */
  key: string;
  /** Bold, naming the days: "Chilly Tue night". */
  title: string;
  /** The title's colour; null for the ink. */
  token: VerdictToken | null;
  /** A danger: drawn with the act tag. */
  act: boolean;
  /** The why, then what to do where there is something. */
  text: string;
}

/**
 * A plant's week as one caption line the server writes (B136, services/plants/plantWeekCaption): the lead names the weather and
 * the days, the closed sentence is the step, "more" opens the story and "less" shuts it. Every word is the server's, in the
 * brief's language.
 */
export interface BriefWeekCaption {
  /** The weather and the days ("Cold nights Mon–Thu"), drawn bold after the icon. */
  lead: string;
  /** The closed sentence: the step to take ("Harvest what you can, or cover it those nights."). */
  sentence: string;
  /** The story that "more" opens: its paragraphs, the last the "rest". */
  story: string[];
  /** The word that opens the story ("… more"). */
  more: string;
  /** The word that shuts it ("… less"). */
  less: string;
}

/** A plant's week, said. */
export interface BriefWeek {
  /** How the plant is taking the week. */
  state: WeekLevel;
  /** The state's colour: Thriving good, Stressed the way it leans, At risk its danger's; else null. */
  token: VerdictToken | null;
  /** How much it asks: urgent for a danger, caution for anything else to act on, settled for nothing. */
  severity: 'urgent' | 'caution' | 'settled';
  /** 2, 1 or 0, the same. */
  rank: number;
  /** Everything to act on: dangers first, then harm and rain, water last. */
  marks: BriefMark[];
  /** The halves that only slow the plant: the grid's tint, each with its lean. */
  slow: SlowHalf[];
  /** The card's "Grows slowly" item's title over those it has a reading for, or null. */
  slowTitle: string | null;
  /** The card's paragraphs, most serious first. */
  items: BriefWeekItem[];
  /** The range it grows best in, for the key ("65–85°F"); null without a range. */
  best: string | null;
  /** The week as one caption line (B136). */
  caption: BriefWeekCaption;
  /** The icon the server names for the caption (a sky key such as "snowflake" or "sun"; WeekIcon draws it). */
  icon: string;
}

/** A plant's planting dates, said. */
export interface BriefPlanting {
  /** "Not sure": nothing recorded; the card shows "Planted yet?". */
  unsure: boolean;
  /** Whether a planting window includes today (B84): what the Plants page's "Can plant now" filter matches. */
  canPlantNow: boolean;
  /** The card's line, for a plant at any stage (E13) ("Plant out until early October", or the next window: "Next: Sow late March to April", B84) and its tooltip, or null for a crop with no window. */
  line: { text: string; title: string } | null;
  /** The sheet the line and the ⋯ menu open. */
  sheet: {
    /** For your area, or a rough date. */
    note: string;
    /** Every window, the soonest season first. */
    windows: Array<{
      /** Unique: season and method. */
      key: string;
      /** The season's name over its first window, when the crop has more than one; null otherwise. */
      season: string | null;
      /** Its days: "Plant out Aug 17 – Oct 6" (a flower's rough date in its own words). */
      text: string;
      /** Its possible days where they differ from those shown, or null. */
      possible: string | null;
      /** Why it falls where it does, or null. */
      reason: string | null;
    }>;
  };
}

/** One live plant in the brief. */
export interface BriefPlant {
  /** Its planting stage. */
  stage: PlantingStage;
  /** Its week, or null without one (sheltered, at Planning, just added, or no forecast). */
  week: BriefWeek | null;
  /** Its planting dates, or null (a crop with no dates). */
  planting: BriefPlanting | null;
  /** E5: a plant at Planning only: the stage its "Plant now" records (by its window's method), decided by the server; null otherwise. */
  plantNow: { stageId: number; stageType: string; method: string } | null;
  /** B110: what its Public switch and Share count show, for the whole garden in this one read (so no card reads GET /shares/plant/:id); the same answer that route gives. Null when the server could not read it: the Public switch then reads for itself. */
  sharing: PlantSharing | null;
  /** W24 Job A: Grow knows its crop (its plant type has a crop definition), so its card offers the Care strip. */
  about: boolean;
  /** B136: its to-dos, as the server counts them. */
  todos: {
    /** An own to-do about it is due today (a yes or no): the To-dos tile is tinted. */
    dueToday: boolean;
    /** How many open own to-dos it has (0 for none): the To-dos tile's count. */
    open: number;
  };
  /** B136: the Care strip's two facts, as the server writes them. */
  care: {
    /** "likes 65–85°F · water about weekly". */
    line: string;
  };
}

/** One mark over the garden. */
export interface GardenMark {
  /** The mark's family: what the garden is filtered by. */
  key: string;
  /** Its words without days. */
  what: string;
  /** Its title over every plant's days. */
  title: string;
  /** Temperature or water, with code, for the icon. */
  kind: 'temperature' | 'water';
  /** The verdict code its icon is drawn from. */
  code: string;
  /** Its colour, or null (wind). */
  token: VerdictToken | null;
  /** A danger: drawn solid. */
  solid: boolean;
  /** Harm: drawn outlined. */
  harm: boolean;
  /** The plants that carry it. */
  plantIds: number[];
}

/** What GET /plants/brief answers. */
export interface PlantsBrief {
  /** The day it was said for, "YYYY-MM-DD". */
  date: string;
  /** Its language. */
  lang: string;
  /** The unit the outlook was read in. */
  unit: 'F' | 'C';
  /** Whether the outlook could be read: without it there are no weeks, and nothing is ranked. */
  hasOutlook: boolean;
  /** A day's rain from which the card's rain row marks it, inches (a rain day). */
  rainShownInches: number;
  /** The server is rebuilding the garden's outlook as this answers (the app shows "Updating your garden..."). */
  updating: boolean;
  /** Each live plant, by plant_obj_id. */
  plants: Record<string, BriefPlant>;
  /** The garden's marks, dangers first. */
  marks: GardenMark[];
  /** The "Needs you first" order, every plant, live and archived. */
  order: number[];
  /** W24 Job A: the ids, ascending, of the garden's archived plants whose crop Grow knows, so their cards offer About this plant. */
  aboutArchived: number[];
}

/**
 * Reads the plants brief, for the garden's own day (the server's, from the
 * garden's time zone: no day is sent; architecture audit A31).
 *
 * @param zip - The garden's postcode (useGardenPostcode); sent only in the person's own garden (gardenZip).
 * @param lang - The words' language.
 * @param unit - The unit the app reads the outlook in.
 * @returns The brief.
 */
export async function getPlantsBrief(zip: string, lang: string, unit: 'F' | 'C'): Promise<PlantsBrief> {
  const response = await apiClient.get<PlantsBrief>('/plants/brief', {
    params: { zip: gardenZip(zip), lang, unit },
  });
  return response.data;
}
